/* eslint-disable no-console */
import * as OT from '@opentok/client';
import logger from '../logger-rum';
import { setBandwidth } from '../logger';
import { ConnectedUser } from '../../stores/types/connection';
import printOTDebugLogForStreamId from '../opentok-debug-helper';
import type { GreenroomUser } from '../../stores/types/session';
import { AVHookInterface, PartialAV } from '../../contexts/audio-video';
import { OPENTOK_CONSTANTS } from '../../types/constants';
import { LogLevel } from '../../stores/types/types';
import type { TotalOTStats } from '../../stores/types/opentok';
import type {
	CpuPerformanceState,
	PublisherHealth,
	SubscriberHealth,
	TransportHealth,
} from '../../stores/types/opentok-health';
import { getLiveDevices, getCameraStreamNoAudio } from './video-streams';
import MediaPipeBackground from './mediapipe-virtual-background';
import store from '../../stores/main';
import { VirtualBg } from '../../stores/types/content';
import { setRestrictedUserState } from '../../stores/actions/session';
import { setShouldRepublishVideo, setMainVideoStream } from '../../stores/actions/video';
import { RESTRICTED_USER_STATES } from '../../stores/reducers/session';

const BRANDLIVE_OPENTOK_API_KEY = process.env.REACT_APP_OPENTOK_API_KEY ?? '';
const isDebug = window.location.search.includes('debug=opentok');
let badReload = false;

interface Session extends OT.Session {
	isConnected: Function;
}

interface Publisher extends OT.Publisher {
	streamId: string | number;
	primaryStreamId: string | number;
	/* eslint-disable-next-line no-unused-vars */
	getStats(callback: (error?: OT.OTError, stats?: OT.PublisherStatsArr) => void): void;
	isLoading: () => boolean;
	setVideoMediaProcessorConnector(
		connector: OT.MediaProcessorConnector | null
	): Promise<void>;
	_: {
		webRtcStream: Function;
	}
}

export interface Subscriber extends OT.Subscriber {
	streamId: string;
	_: {
		unblockAudio: Function;
	}
}

interface RawStats {
	id: string;
	videoBits: number;
	videoPackets: number;
	videoPacketsLost: number;
	audioBits: number;
	audioPackets: number;
	audioPacketsLost: number;
	frames: number;
	lastTime: number;
}

interface LastStat {
	videoBits: number,
	videoPackets: number;
	videoPacketsLost: number;
	audioBits: number;
	audioPackets: number;
	audioPacketsLost: number;
	frames: number;
	time: number;
}

interface LastStats {
	[key: string]: LastStat;
}

interface OTStats {
	videoBitrate: number;
	videoPacketLossRatio: number;
	audioBitrate: number;
	audioPacketLossRatio: number;
	frameRate: number;
	resolution: OT.Dimensions;
	guid: string;
}

export type { TotalOTStats } from '../../stores/types/opentok';

export interface VideoQuality {
	width: number;
	height: number;
	frameRate: number;
}

function debugLog(tag: string, ...args: any[]) {
	if (isDebug) {
		console.log(`%c${tag}\n`, 'color: red; font-weight: bold', ...args);
	}
}

export type Subscribers = {
	[key: string]: Subscriber;
}

const asyncScreenshareData = (): Promise<boolean> => new Promise((res) => {
	OT.checkScreenSharingCapability((r: OT.ScreenSharingCapabilityResponse) => res(r?.supported));
});

const asyncStats = (subscriber: Subscriber): Promise<RawStats> => new Promise((resolve) => {
	subscriber?.getStats((error?: OT.OTError, stats?: OT.SubscriberStats) => {
		if (stats?.video) {
			resolve({
				id: String(subscriber.streamId),
				videoBits: stats!.video.bytesReceived * 8 || 0,
				videoPackets: stats!.video.packetsReceived || 0,
				videoPacketsLost: stats!.video.packetsLost || 0,
				audioBits: stats!.audio.bytesReceived * 8 || 0,
				audioPackets: stats!.audio.packetsReceived || 0,
				audioPacketsLost: stats!.audio.packetsLost || 0,
				frames: 0,
				lastTime: Date.now(),
			});
		} else {
			resolve({
				id: String(subscriber.streamId),
				videoBits: 0,
				videoPackets: 0,
				videoPacketsLost: 0,
				audioBits: 0,
				audioPackets: 0,
				audioPacketsLost: 0,
				frames: 0,
				lastTime: Date.now(),
			});
		}
	});
});

const asyncStatsPub = (
	publisher: Publisher,
): Promise<RawStats> => new Promise((resolve) => {
	publisher?.getStats((error?: OT.OTError, stats?: OT.PublisherStatsArr) => {
		if (stats && stats[0]?.stats) {
			resolve({
				id: String(publisher.streamId),
				videoBits: (stats[0].stats?.video.bytesSent ?? 0) * 8,
				videoPackets: stats[0].stats.video.packetsSent || 0,
				videoPacketsLost: stats[0].stats.video.packetsLost || 0,
				audioBits: (stats[0].stats?.audio.bytesSent ?? 0) * 8,
				audioPackets: stats[0].stats.audio.packetsSent || 0,
				audioPacketsLost: stats[0].stats.audio.packetsLost || 0,
				frames: 0,
				lastTime: Date.now(),
			});
		} else {
			resolve({
				id: String(publisher.streamId),
				videoBits: 0,
				videoPackets: 0,
				videoPacketsLost: 0,
				audioBits: 0,
				audioPackets: 0,
				audioPacketsLost: 0,
				frames: 0,
				lastTime: Date.now(),
			});
		}
	});
});

const getPubOrSubStats = (
	pubOrSub: Subscriber | OT.Publisher,
): Promise<OT.PublisherStatsArr | OT.SubscriberStats | undefined> => new Promise((resolve) => {
	pubOrSub?.getStats((err, stats) => {
		if (err || !stats) resolve(undefined);
		resolve(stats);
	});
});

/**
 * Narrows an SDK TransportStats to the fields the dashboard reports.
 * Returns undefined rather than a zeroed object so a missing transport reads as
 * "not measured" instead of "measured as bad".
 */
const toTransportHealth = (transport: any): TransportHealth | undefined => {
	if (!transport) return undefined;
	return {
		networkCondition: transport.networkCondition ?? 'unknown',
		networkConditionReason: transport.networkConditionReason ?? 'none',
		connectionEstimatedBandwidth: transport.connectionEstimatedBandwidth ?? 0,
	};
};

export const getCurrentVirtualBG = (): OT.VideoFilter | undefined => {
	const {
		ContentReducer: { selectedVBG },
	} = store.getState();
	if (selectedVBG?.blur) {
		return {
			type: 'backgroundBlur',
			blurStrength: selectedVBG.blur,
		};
	}
	if (selectedVBG?.url) {
		return {
			type: 'backgroundReplacement',
			backgroundImgUrl: selectedVBG.url,
		};
	}
	return undefined;
};

export default class OpenTok {
	public user: GreenroomUser | null;

	public session: Session;

	public publisher: Publisher | null = null;

	public screensharePublisher: Publisher | null = null;

	// Stored when startScreensharing is called with a custom track so getScreenshareTrack()
	// can clone it directly instead of going through the unreliable internal webRtcStream() API.
	private screenshareSourceTrack: MediaStreamTrack | null = null;

	// Stored audio track from a custom-source screenshare (e.g. tab audio). null means the
	// screenshare has no audio (screen-only), in which case audio is disabled to prevent
	// the publisher from capturing the mic as a fallback.
	private screenshareSourceAudioTrack: MediaStreamTrack | null = null;

	public subscribers: Subscribers;

	/**
	 * Machine-level pressure from the session's cpuPerformanceChanged event.
	 * Event-driven, so it has to be latched here - getStats() does not report it.
	 */
	private cpuPerformanceState: CpuPerformanceState | undefined;

	/** MOS scores per subscribed streamId, latched from qualityScoreChanged. */
	private subscriberQualityScores: {
		[streamId: string]: { audioQualityScore?: number; videoQualityScore?: number };
	} = {};

	/**
	 * Called when the SDK reports a degradation, so the client can push a report
	 * immediately instead of waiting for the dashboard to ask again.
	 */
	public onHealthDegradation: (() => void) | null = null;

	private lastSubscriberState: Subscribers;

	// Per-stream timestamp of the last proactive resubscribe, to bound retry
	// frequency (BDEV-4780 #8/#9).
	private lastResubscribeAt: Record<string, number> = {};

	private static readonly RESUBSCRIBE_COOLDOWN_MS = 5000;

	public onVideoElementCreated: (event: any, isPublisher: boolean) => any;

	private readonly onStats: (event: any) => any;

	private readonly onPublisherRemoved: (event: any) => any;

	private readonly debugListener: (event: any) => any;

	private readonly token: string;

	public sharingScreen: boolean = false;

	// Which input is suspended (track ended via permission revoke) while the publisher
	// is kept alive — drives the restricted state and what to restore on re-grant.
	public videoSuspended: boolean = false;

	public audioSuspended: boolean = false;

	private lastStats: LastStats = {};

	public stats: TotalOTStats = {};

	private statsInterval: NodeJS.Timeout;

	public lastRes: number[] = [1920, 1080];

	public lastFrameRate: number = 30;

	public videoMediaStream: MediaStream | null = null;

	public mediapipeBackground: MediaPipeBackground | null = null;

	private mediapipeViaConnector = false;

	public debugOpentokSubscriptions: boolean = false;

	public isEmbed: boolean = false;

	private setAV: AVHookInterface['setAV'];

	private removeAV: AVHookInterface['removeAV'];

	public streams: Map<string, OT.Stream> = new Map<string, OT.Stream>();

	// set of publishers this client has initialized
	public myPublishers: Array<OT.Publisher> = [];
	// private myPublishers: Map<string, OT.Publisher> = new Map<string, OT.Publisher>();

	public checkScreenshare: () => Promise<boolean> = asyncScreenshareData;

	private getSubscriberStats: (subscriber: Subscriber) => Promise<RawStats> = asyncStats;

	private getPublisherStats: (publisher: Publisher) => Promise<RawStats> = asyncStatsPub;

	constructor(
		user: GreenroomUser | null,
		sessionId: string,
		token: string,
		setAVState: AVHookInterface['setAV'],
		removeAVState: AVHookInterface['removeAV'],
		onVideoElementCreated: (event: any, isPublisher: boolean) => any,
		onStats: (event: any) => any,
		onPublisherRemoved?: (event: any) => any,
		debugOpentokSubscriptions: boolean = false,
		isEmbed: boolean = false,
	) {
		this.user = user;
		this.token = badReload ? 'badtoken' : token;
		this.session = OT.initSession(BRANDLIVE_OPENTOK_API_KEY, sessionId, {
			singlePeerConnection: isEmbed ? false : true
		}) as Session;
		this.session.on('sessionConnected', () => this.sessionListeners());
		// Machine pressure is only ever delivered as an event, so latch it here for
		// collectHealthStats() to read. A presenter's laptop struggling looks
		// identical to a bad network without it.
		this.session.on('cpuPerformanceChanged', (e: any) => {
			this.cpuPerformanceState = e?.cpuPerformanceState;
			if (this.cpuPerformanceState === 'serious' || this.cpuPerformanceState === 'critical') {
				this.onHealthDegradation?.();
			}
		});
		this.onVideoElementCreated = onVideoElementCreated;
		this.onStats = onStats;
		this.onPublisherRemoved = onPublisherRemoved || (() => { });
		this.debugListener = this.debugHotkey.bind(this);
		// update the bitrate stats every 5 seconds
		this.statsInterval = setTimeout(this.checkStats, 1000);
		this.debugOpentokSubscriptions = debugOpentokSubscriptions;
		this.isEmbed = isEmbed;
		this.setAV = setAVState;
		this.removeAV = removeAVState;
		this.subscribers = {};
		this.lastSubscriberState = {};

		if (isDebug) {
			// When debugging with the 'debug=opentok' query string,
			// you can simulate a continuous failing SOCKET connection
			// with the E hotkey and an immediate reconnection with the R hotkey.
			window.addEventListener('keydown', this.debugListener);
		}
	}

	log(
		level: LogLevel,
		message: string,
		logData: { [key: string]: any,
	},
	) {
		const commonLogInfo = `OpenTok: [${this.publisher?.stream?.streamId}] ${this?.user?.email}`;
		if (isDebug) {
			console.log(`${commonLogInfo} ${message}`);
		}

		const gcLog = {
			family: 'OpenTok',
			location: 'opentok-connection',
			...logData,
		};

		const logFn = (logger as any)[level] ?? logger.log;
		logFn(`${commonLogInfo} ${message}`, gcLog);
	}

	publisherTrackEndLog = (trackType: string, streamId: string) => {
		this.log(
			'info',
			`publisher ${trackType} has ended - streamId: ${streamId}`,
			{ function: `publisher.on videoElementCreated => ${trackType} ended` },
		);
	};

	// Secondary revoke detector: a permission revoke ends the track (Vonage doesn't fire
	// mediaStopped for a lock-icon change). { once: true }, so restoreVideo/AudioSource
	// must re-attach to the replacement track or a second revoke goes undetected.
	attachVideoEndedListener = (
		publisher: OT.Publisher,
		videoTrack?: MediaStreamTrack,
		isScreensharePublisher: boolean = false,
	) => {
		videoTrack?.addEventListener(
			'ended',
			() => {
				// Only the ACTIVE publisher may set the shared suspend flags — a replaced
				// publisher's teardown also ends its tracks, and acting on that would
				// falsely suspend the new publisher and re-trigger republish cycles.
				if (!isScreensharePublisher && publisher === this.publisher) {
					this.videoSuspended = true;
					publisher.publishVideo(false);
					store.dispatch(setRestrictedUserState(this.computeRestrictedState()));
				}
				this.publisherTrackEndLog('videoTrack', publisher?.stream?.streamId ?? 'unknown');
			},
			{ once: true },
		);
	};

	attachAudioEndedListener = (
		publisher: OT.Publisher,
		audioTrack?: MediaStreamTrack,
		isScreensharePublisher: boolean = false,
	) => {
		audioTrack?.addEventListener(
			'ended',
			() => {
				// Same active-publisher guard as the video listener above.
				if (!isScreensharePublisher && publisher === this.publisher) {
					this.audioSuspended = true;
					publisher.publishAudio(false);
					store.dispatch(setRestrictedUserState(this.computeRestrictedState()));
				}
				this.publisherTrackEndLog('audioTrack', publisher?.stream?.streamId ?? 'unknown');
			},
			{ once: true },
		);
	};

	publisherListeners(publisher: OT.Publisher, isScreensharePublisher: boolean) {
		// sets up listeners for a session or publisher
		publisher.on('streamCreated', (e) => {
			this.onAVDataUpdate(e);
			this.streams.set(e.stream.streamId, e.stream);
		});
		// Our own uplink degrading is the single most useful thing to push to the
		// health dashboard without waiting to be asked.
		publisher.on('networkConditionChanged', (e: any) => {
			const condition = e?.statsContainer?.stats?.mediaLink?.transport?.networkCondition;
			if (condition === 'critical' || condition === 'warning') {
				this.onHealthDegradation?.();
			}
		});
		publisher.on('videoElementCreated', (e) => {
			if (e.element instanceof HTMLVideoElement && e.element.srcObject instanceof MediaStream) {
				const [videoTrack] = e.element.srcObject.getVideoTracks();
				const [audioTrack] = e.element.srcObject.getAudioTracks();
				this.attachVideoEndedListener(publisher, videoTrack, isScreensharePublisher);
				this.attachAudioEndedListener(publisher, audioTrack, isScreensharePublisher);
			}
			this.onVideoElementCreated(e, !isScreensharePublisher);
		});
		if (!isScreensharePublisher) {
			publisher.on('mediaStopped', (event: any) => {
				// Keep the publisher alive when one device's permission is revoked — destroying it
				// would streamDestroyed the feed for all peers. Instead mute the affected side
				// (publishVideo/Audio(false)); peers see streamPropertyChanged and the feed stays put.
				// Vonage's documented mediaStopped recovery: preventDefault now, restore the source later.
				event.preventDefault();

				// A replaced publisher's teardown also stops its media — only the active
				// publisher may set the shared suspend flags.
				if (publisher !== this.publisher) return;

				const { track } = event;
				if (track?.kind === 'video') {
					this.videoSuspended = true;
					publisher.publishVideo(false);
				} else if (track?.kind === 'audio') {
					this.audioSuspended = true;
					publisher.publishAudio(false);
				}

				store.dispatch(setRestrictedUserState(this.computeRestrictedState()));

				logger.log(
					'info',
					`mediaStopped (publisher kept alive): track.kind=${track?.kind}`,
					{ function: 'publisherListeners mediaStopped' },
				);
			});
		}
		publisher.on('streamDestroyed', (e) => {
			const { stream } = e as unknown as any;
			if (!stream) return;

			if (stream.streamId) {
				this.removeAV(stream.streamId);
				this.streams.delete(stream.streamId);
			} else {
				console.log('ERROR - no stream id when streamDestroyed');
			}

			this.onPublisherRemoved(e);
			publisher.off();
		});
		publisher.on('muteForced', () => {
			const currAudio = publisher?.stream?.hasAudio;
			publisher.publishAudio(!currAudio);
		});
	}

	signalListeners() {
		this.session.on('signal', (e: any) => {
			try {
				const event = e;
				event.data = JSON.parse(e.data);
				const signalEvent = event.type.split('signal:')[1];
				switch (signalEvent) {
					case OPENTOK_CONSTANTS.SIGNALS.FORCE_SET_AUDIO: {
						const { streamId, value } = event.data;
						const publisher = this.myPublishers.find((p) => p.stream?.streamId === streamId);
						publisher?.publishAudio(value);
						break;
					}
					case OPENTOK_CONSTANTS.SIGNALS.FORCE_SET_VIDEO: {
						const { streamId, value } = event.data;
						const publisher = this.myPublishers.find((p) => p.stream?.streamId === streamId);
						publisher?.publishVideo(value);
						break;
					}
					default: {
						break;
					}
				}
			} catch (exception) {
				if (this.debugOpentokSubscriptions) {
					console.log(exception);
				}
			}
		});
	}

	sessionListeners() {
		this.signalListeners();
		this.session.on('streamCreated', (e) => {
			this.onAVDataUpdate(e);
			this.streams.set(e.stream.streamId, e.stream);
		});
		this.session.on('streamDestroyed', (e) => {
			this.removeAV(e.stream.streamId);
			this.streams.delete(e.stream.streamId);
		});
		this.session.on('streamPropertyChanged', (e) => {
			this.onAVDataUpdate(e);
			this.log(
				'info',
				`streamPropertyChanged ${e.changedProperty} ${e.newValue} ${e.oldValue}`,
				{
					function: 'streamPropertyChanged',
				},
			);
			if (e.changedProperty === 'hasVideo'
				&& this.publisher
				&& this.publisher?.stream?.videoDimensions
				&& e.stream.streamId === this.publisher?.stream?.streamId
				&& (
					(
						this.lastRes[0] !== this.publisher?.stream?.videoDimensions.width
						&& this.lastRes[1] !== this.publisher?.stream?.videoDimensions.height
					)
					|| this.lastFrameRate !== this.publisher?.stream?.frameRate
				)
				// Canvas tracks from the MediaPipe pipeline ignore applyConstraints.
				&& !this.mediapipeBackground?.isActive
			) {
				this.videoMediaStream?.getVideoTracks()[0]?.applyConstraints(
					{
						width: { ideal: this.lastRes[0] },
						height: { ideal: this.lastRes[1] },
						frameRate: { exact: this.lastFrameRate },
					},
				);
			}
		});
	}

	onAVDataUpdate(e: any) {
		if (e?.stream?.id) {
			const isLocal = e.stream.id === this.publisher?.stream?.streamId;
			const avData: PartialAV = {
				id: isLocal ? 'local' : e.stream.id,
			};
			// For the LOCAL publisher, a transport hasVideo/hasAudio=false is never a reliable
			// signal to turn the stored PREFERENCE off. It fires on a permission suspend, on
			// transient renegotiation, and in the re-grant restore race (the suspend's late
			// hasVideo=false event arrives after videoSuspended is cleared). Persisting it flips
			// the user's preference off, which is self-perpetuating: getAV() then feeds video=false
			// into every subsequent (re)publish, so the feed comes back audio-only on every reload.
			// The local preference is owned by the user's toggle (toggleVideo/toggleAudio); only let
			// transport turn it ON. Remote streams still mirror their transport state fully. (BDEV-4780)
			if (isLocal) {
				if (e.stream.hasVideo) avData.video = true;
				if (e.stream.hasAudio) avData.audio = true;
			} else {
				avData.video = e.stream.hasVideo ?? false;
				avData.audio = e.stream.hasAudio ?? false;
			}
			this.setAV(avData);
		}
	}

	debugHotkey(e: any) {
		if (e.repeat) return;

		if (e.code === 'KeyE') {
			// disconnect from session and reload with invalid token
			if (this.session.isConnected()) this.session.disconnect();
			badReload = !badReload;
		} else if (e.code === 'KeyR') {
			// disconnect from session and reload
			if (this.session.isConnected()) this.session.disconnect();
		} else if (e.code === 'KeyT') {
			debugLog(BRANDLIVE_OPENTOK_API_KEY);
		}
	}

	subscribeOnly = () => new Promise((resolve, reject) => {
		this.session.connect(this.token, (err?: OT.OTError) => {
			if (err) reject(err);
			else resolve(true);
		});
	});

	destroy() {
		clearInterval(this.statsInterval);
		this.session.disconnect();
		this.session.off();
		this.mediapipeBackground?.destroy();
		this.mediapipeBackground = null;
		this.mediapipeViaConnector = false;
		if (isDebug) {
			window.removeEventListener('keydown', this.debugListener);
		}
	}

	checkStats = async () => {
		this.stats = await this.getOTStats();
		this.onStats(this.stats);
		this.statsInterval = setTimeout(this.checkStats, 5000);
	};

	onStreamAdded = (callback: Function) => {
		if (this.debugOpentokSubscriptions) {
			console.log('stream added');
		}

		this.session.on('streamCreated', (e: any) => {
			callback(e);
		});
	};

	debugOTStreamRemoved = (stream: OT.Stream) => {
		if (this.debugOpentokSubscriptions) {
			const id = stream?.name?.split(',')?.[0] ?? 0;
			const connectedUser: any = window?.debugOT?.connectedUsers?.find(
				(cu: ConnectedUser) => Number(cu.gr_user) === Number(id)
					|| cu?.screenShareWebrtcId === stream.streamId,
			);
			const altText = connectedUser?.screenShareWebrtcId === stream.streamId
				? '\'s Screen Share'
				: '';

			if (this.debugOpentokSubscriptions) {
				console.log(`removed ${
					connectedUser
						? `${connectedUser.first_name} ${connectedUser.last_name}${altText}`
						: stream.streamId
				}`);
			}
		}
	};

	/* eslint-disable-next-line no-unused-vars */
	onStreamRemoved = (callback: (event: any) => void) => {
		this.session.on('streamDestroyed', (e) => {
			this.log(
				'info',
				`stopped subscribing to stream ${e.stream.streamId}`,
				{
					function: 'onStreamRemoved',
				},
			);

			const removed = this.subscribers[e.stream.streamId];

			if (removed) {
				if (this.debugOpentokSubscriptions && removed.stream) {
					this.debugOTStreamRemoved(removed.stream);
				}
				// Remove all listeners from removed stream
				removed.off();
				delete this.subscribers[e.stream.streamId];
				delete this.lastResubscribeAt[e.stream.streamId];
			}
			callback(e);
		});
	};

	/* eslint-disable-next-line no-unused-vars */
	onSessionDisconnect = (callback: (event: any) => void) => {
		this.session.on('sessionDisconnected', (e) => {
			this.log(
				'info',
				`disconnected from session ${this.session.sessionId}`,
				{
					function: 'onSessionDisconnect',
				},
			);
			if (this.debugOpentokSubscriptions) console.log('session disconnected ');
			if (this.debugOpentokSubscriptions) console.log('attempting to handle reconnect ');
			callback(e);
		});
	};

	/* eslint-disable-next-line no-unused-vars */
	onReconnected = (callback: (event: any) => void) => {
		this.session.on('reconnected', (e) => {
			this.log(
				'info',
				`reconnected to session ${this.session.sessionId}`,
				{
					function: 'onReconnected',
				},
			);
			if (this.debugOpentokSubscriptions) console.log('session reconnected');
			callback(e);
		});
	};

	debugOTSubscribeToStream = (stream: OT.Stream, resubscribed = false) => {
		if (this.debugOpentokSubscriptions) {
			const id = stream?.name?.split(',')?.[0] ?? 0;
			const connectedUser: any = window?.debugOT?.connectedUsers?.find(
				(cu: ConnectedUser) => Number(cu.gr_user) === Number(id)
					|| cu?.screenShareWebrtcId === stream.streamId,
			);
			const subType = !resubscribed ? 'subscribed' : 'resubscribed';
			const altText = connectedUser?.screenShareWebrtcId === stream.streamId
				? '\'s Screen Share'
				: '';

			if (this.debugOpentokSubscriptions) {
				console.log(`${subType} to ${
					connectedUser
						? `${connectedUser.first_name} ${connectedUser.last_name}${altText}`
						: stream.streamId
				}`);
			}
		}
	};

	subscribeToStream = (
		stream: OT.Stream,
		companionMode: boolean = false,
		embed: boolean = false,
	) => {
		this.log(
			'info',
			`subscribed to stream ${stream.streamId}`,
			{
				function: 'subscribeToStream',
			},
		);

		if (this.debugOpentokSubscriptions) this.debugOTSubscribeToStream(stream);

		return new Promise((resolve, reject) => {
			// We may want to use this for sip
			// const subscriber = this.session.subscribe(stream, isSip ? 'sip-container' : undefined, {
			//	insertDefaultUI: isSip,
			// }
			let isSipStream = false;
			try {
				const connData = JSON.parse(stream.connection?.data ?? '{}');
				isSipStream = connData?.sip === true;
				if (isSipStream) {
					console.log('[subscribeToStream] subscribing to SIP stream', {
						streamId: stream.streamId,
						name: stream.name,
						hasAudio: stream.hasAudio,
						hasVideo: stream.hasVideo,
						companionMode,
						embed,
					});
				}
			} catch (_) { /* non-JSON connection data */ }

			const subscriber = this.session.subscribe(stream, undefined, {
				insertDefaultUI: false,
				audioVolume: embed ? 0 : 100, // start embed at 0
			}, (error?: OT.OTError) => {
				if (error) {
					this.log(
						'error',
						`subscribed to stream ${stream.streamId} failure with error ${error.name} ${error.message}`,
						{
							function: 'subscribeToStream session.subscribe',
						},
					);
					if (isSipStream) {
						console.error('[subscribeToStream] SIP stream subscription FAILED', error);
					}
					reject(error);
				} else {
					if (isSipStream) {
						console.log('[subscribeToStream] SIP stream subscription OK, audio enabled:', !companionMode);
					}
					resolve(true);
				}
			}) as Subscriber;

			subscriber.subscribeToAudio(!companionMode);

			subscriber.on('videoElementCreated', (e) => this.onVideoElementCreated(e, false));

			// Health dashboard signals. Both are event-only - getStats() never reports a
			// MOS score, and networkCondition is worth pushing the moment it degrades
			// rather than on the dashboard's next poll.
			subscriber.on('qualityScoreChanged', (e: any) => {
				this.subscriberQualityScores[stream.streamId] = {
					audioQualityScore: e?.qualityScore?.audioQualityScore,
					videoQualityScore: e?.qualityScore?.videoQualityScore,
				};
			});
			subscriber.on('networkConditionChanged', (e: any) => {
				const condition = e?.stats?.mediaLink?.transport?.networkCondition;
				if (condition === 'critical' || condition === 'warning') {
					this.onHealthDegradation?.();
				}
			});

			subscriber.on(
				'videoDisabled',
				(e: any) => {
					debugLog('videoDisabled', e);
					this.log(
						'info',
						`subscriber videoDisabled ${stream.streamId}`,
						{
							function: 'subscribeToStream on videoDisabled',
						},
					);
				},
			);
			subscriber.on(
				'videoEnabled',
				(e: any) => {
					this.log(
						'info',
						`subscriber videoEnabled ${stream.streamId}`,
						{
							function: 'subscribeToStream on videoEnabled',
						},
					);
					debugLog('videoEnabled', e);
				},
			);
			subscriber.on(
				'videoDisableWarning',
				() => {
					this.log(
						'info',
						`subscriber videoDisableWarning ${stream.streamId}`,
						{
							function: 'subscribeToStream on videoDisableWarning',
						},
					);
				},
			);
			// A remote peer resetting camera/mic permissions fails this subscriber. Without an 'error'
			// listener, OT's EventEmitter re-throws it as an "Unhandled error", which wedges the
			// subscriber state machine ('Failed'->'Failed') and leaves the feed dropped until OT's own
			// ~60s recovery. Handling it here keeps the error contained and logged (BDEV-4780).
			subscriber.on(
				'error',
				(e: any) => {
					this.log(
						'error',
						`subscriber error ${stream.streamId}: ${e?.name ?? ''} ${e?.message ?? ''}`,
						{
							function: 'subscribeToStream on error',
						},
					);
					// Proactively recover instead of waiting OT's ~60s internal recovery. (BDEV-4780 #8/#9)
					this.maybeResubscribe(stream.streamId);
				},
			);

			this.subscribers = {
				...this.subscribers,
				[stream.streamId]: subscriber,
			};
		});
	};

	resubscribeToStream = async (subscriber: Subscriber) => {
		const { stream } = subscriber;
		if (stream) {
			this.log(
				'info',
				`resubscribing to stream ${stream.streamId}`,
				{
					function: 'resubscribeToStream',
				},
			);

			if (this.debugOpentokSubscriptions) {
				this.debugOTSubscribeToStream(stream, true);
			}

			this.session.unsubscribe(subscriber);
			await this.subscribeToStream(stream);
		}
	};

	// A failing subscriber (peer permission reset, "1501 NativeVideoElementWrapper is
	// destroyed") otherwise waits on OT's ~60s internal recovery. Resubscribe proactively.
	// The per-stream cooldown bounds retry FREQUENCY (at most once per
	// RESUBSCRIBE_COOLDOWN_MS) so a still-failing stream can't spin a tight
	// unsubscribe/subscribe storm — but retries are intentionally unbounded in COUNT: a
	// peer's stream returns only after their own publisher recovers (many seconds), and
	// giving up would re-strand a recoverable feed. A genuinely-gone stream is removed from
	// this.subscribers by streamDestroyed, so the guard below early-returns for it.
	// (BDEV-4780 #8/#9)
	maybeResubscribe = (streamId: string) => {
		const subscriber = this.subscribers[streamId];
		if (!subscriber || !subscriber.stream) return;
		const now = Date.now();
		const last = this.lastResubscribeAt[streamId] ?? 0;
		if (now - last < OpenTok.RESUBSCRIBE_COOLDOWN_MS) return;
		this.lastResubscribeAt[streamId] = now;
		this.resubscribeToStream(subscriber).catch((e) => {
			this.log(
				'error',
				`maybeResubscribe: resubscribe failed for ${streamId}: ${e?.name ?? ''} ${e?.message ?? ''}`,
				{ function: 'maybeResubscribe' },
			);
		});
	};

	mediaStopped = (event: any) => {
		event.preventDefault();

		if (this.debugOpentokSubscriptions) {
			console.log(`event: ${JSON.stringify(event)}`);
		}
	};

	startStream = (
		audioPref: boolean,
		videoPref: boolean,
		selectedAudio?: string,
		selectedVideo?: string,
		name?: string,
		isRefresh: boolean = false, // if the client errors, we check session connection and poll for the new publisher
		quality: VideoQuality = { width: 1920, height: 1080, frameRate: 30 },
		useAdvancedAudio: boolean = false,
	): Promise<Publisher | null> => new Promise((resolve, reject) => {
		const createNewStream = async () => {
			try {
				if (!this.session.isConnected()) {
					await this.startSession();
				}
			} catch (e) {
				this.log('error', `error attempting to publish stream error ${e}`, { function: 'startStream' });
				reject(e);
			}

			let interval: NodeJS.Timeout | undefined;

			this.lastRes = [quality.width, quality.height];
			this.lastFrameRate = quality.frameRate;

			// a GUID (opentok_client_id) can be used to identify a single user with multiple connections.
			// this can only be found in the user's local storage
			// and not on publisher and subscriber objects.
			const guid = localStorage.getItem('opentok_client_id');
			const pubName = name ?? String(this.user?.gr_user);

			// check if user's selected devices can produce video and audio
			const [hasVid, hasAud] = await getLiveDevices(selectedVideo, selectedAudio);

			// ── MediaPipe virtual background pipeline ──────────────────────
			// Attempt to initialise the canvas-based segmentation pipeline so
			// the background is baked into the video before OT ingests it.
			// Falls back to OT's native videoFilter API if anything fails.
			let videoSource: string | false | MediaStreamTrack = hasVid ? (selectedVideo ?? false) : false;
			let videoFilter: OT.VideoFilter | undefined;

			if (hasVid && selectedVideo) {
				const { ContentReducer: { selectedVBG } } = store.getState();

				// Only spin up the MediaPipe pipeline when a background is actually
				// selected — non-VBG users get the raw camera track at zero extra cost.
				if (selectedVBG) {
					try {
						// Clean up any pipeline from a previous publish cycle.
						this.mediapipeBackground?.destroy();
						this.mediapipeBackground = new MediaPipeBackground();

						const rawCameraStream = await getCameraStreamNoAudio(selectedVideo);
						const canvasStream = await this.mediapipeBackground.init(rawCameraStream, selectedVBG);
						videoSource = canvasStream.getVideoTracks()[0];
					} catch (mpErr) {
						this.log(
						'error',
						`MediaPipeBackground init failed, falling back to OT videoFilter: ${mpErr}`,
						{ function: 'startStream', error: mpErr },
					);
						this.mediapipeBackground?.destroy();
						this.mediapipeBackground = null;
						this.mediapipeViaConnector = false;

						// OT native filter fallback - cap the feeds resolution at 720p
						videoFilter = getCurrentVirtualBG();
						if (videoFilter) {
							quality.width = quality.width > 1280 ? 1280 : quality.width;
							quality.height = quality.height > 720 ? 720 : quality.height;
						}
					}
				} else {
					// No VBG selected — destroy any leftover pipeline from a previous
					// session and let OT use the raw camera deviceId directly.
					this.mediapipeBackground?.destroy();
					this.mediapipeBackground = null;
					this.mediapipeViaConnector = false;
				}
			}

			// Reuse a CLONE of the outgoing publisher's live mic track instead of re-acquiring
			// the same device by id. On a video-restore republish after a permission re-grant,
			// the mic can already be live (restored in place via restoreAudioSource); a second
			// getUserMedia for a device the outgoing publisher still holds fails on Chrome
			// (OT_CHROME_MICROPHONE_ACQUISITION_ERROR / crbug 4799). clone() shares the source
			// with no new acquisition and survives the old publisher's teardown. (BDEV-4780)
			let audioSourceToUse: string | MediaStreamTrack | undefined = selectedAudio;
			if (hasAud) {
				const currentAudioTrack = await this.publisher?.getAudioSource?.();
				if (currentAudioTrack && currentAudioTrack.readyState === 'live') {
					audioSourceToUse = currentAudioTrack.clone();
				}
			}

			// Preserve the outgoing publisher so a failed publish (OT_TIMEOUT/ICE) can restore it
			// as active and destroy the dead new one — otherwise dead publishers accumulate and
			// starve ICE across revoke/re-grant cycles. On a revoke republish the outgoing
			// publisher's tracks may already have ended, so it is the previously-active publisher,
			// not guaranteed live. (BDEV-4780 #6)
			const previousPublisher = this.publisher;

			this.publisher = OT.initPublisher(undefined, {
				insertDefaultUI: false,
				showControls: false,
				resolution: quality.width === 1920 ? '1920x1080' : '1280x720',
				maxResolution: { width: quality.width, height: quality.height },
				frameRate: 30,
				// enableDtx is false due to this ticket: https://brandlive.atlassian.net/browse/GREEN-5042
				enableDtx: false,
				// comma separated string with the user's gr_user id and OpenTok guid.
				name: guid ? `${pubName},${guid}` : pubName,
				height: quality.height,
				width: quality.width,
				audioBitrate: useAdvancedAudio ? 64000 : 40000,
				audioFallback: {publisher: false, subscriber: false},
				audioSource: hasAud ? audioSourceToUse : false,
				videoSource: hasVid ? videoSource : false,
				publishAudio: hasAud ? audioPref : false,
				publishVideo: hasVid ? videoPref : false,
				disableAudioProcessing: useAdvancedAudio,
				enableStereo: useAdvancedAudio,
				// Off by default. With it on, this publisher's transport stats reach every
				// subscriber, which is the only way a subscriber can tell whether a bad feed
				// is the sender's uplink or its own downlink.
				publishSenderStats: true,
				videoFilter,
			}, (error) => {
				if (error) {
					this.log(
						'error',
						`attempting to publish stream publisher error ${error?.name} ${error?.message}`,
						{ function: 'startStream' },
					);
					console.log(`error publishing OT: ${error?.name} . ${error?.message}`);
				}
			}) as Publisher;

			if (this.publisher) {
				// A freshly published stream has no suspended inputs — clear any flags left
				// over from a previous publisher's revoke/restore cycle.
				this.videoSuspended = false;
				this.audioSuspended = false;
				this.myPublishers.push(this.publisher);
				this.publisherListeners(this.publisher, false);
			}

			const newPublisher = this.publisher;

			this.session.publish(this.publisher, (error?: OT.OTError) => {
				if (error) {
					this.log(
						'error',
						`startStream stream publish session error ${error.name} ${error.message}`,
						{ function: 'startStream session.publish' },
					);
					// The new publisher never completed ICE. Destroy it and drop it from myPublishers
					// so its RTCPeerConnection can't leak and accumulate. Use the publisher captured at
					// creation (not this.publisher) — a concurrent republish may have reassigned
					// this.publisher by the time this async callback fires. Only restore the previously-
					// active publisher if this failed one is still active. (BDEV-4780 #6)
					if (newPublisher && newPublisher !== previousPublisher) {
						try {
							newPublisher.destroy();
						} catch (destroyErr) {
							this.log(
								'error',
								`startStream failed-publisher destroy error ${destroyErr}`,
								{ function: 'startStream session.publish' },
							);
						}
						this.myPublishers = this.myPublishers.filter((p) => p !== newPublisher);
						if (this.publisher === newPublisher) {
							this.publisher = previousPublisher;
						}
					}
					reject(error);
				} else if (isRefresh) {
					console.log('isrefresh');
					interval = setInterval(() => {
						if (this.publisher && this.publisher.streamId) {
							clearInterval(interval as NodeJS.Timeout);

							// this is a hidden method within opentok - returns the outgoing video
							// stream so we can update the resolution
							this.videoMediaStream = this.publisher?._.webRtcStream();
							this.videoMediaStream?.getVideoTracks()[0]?.applyConstraints(
								{
									width: { ideal: quality.width },
									height: { ideal: quality.height },
									frameRate: { ideal: quality.frameRate, max: quality.frameRate },
								},
							).catch((e) => this.log('error', `applyConstraints failed: ${e}`, { function: 'startStream isRefresh' }));

							resolve(this.publisher);
						}
					}, 150);
				} else {
					// Wait for streamId (ICE negotiation complete) before resolving, so any
					// subsequent publisher (e.g. screenshare) is not added to the PeerConnection
					// while the first renegotiation is still in-flight on slow connections.
					interval = setInterval(() => {
						if (this.publisher && this.publisher.streamId) {
							clearInterval(interval as NodeJS.Timeout);
							resolve(this.publisher);
						}
					}, 150);
				}
			});
		};
		createNewStream().catch((e) => reject(e));
	});

	/**
	 * Enable or update the virtual background without restarting the publisher.
	 *
	 * • Pipeline already running → setBackground() only, no publisher disruption.
	 * • Pipeline not yet running → init MediaPipe, attach via
	 *   setVideoMediaProcessorConnector() so OT handles the track swap into all
	 *   peer connections.
	 * • Falls back to OT native videoFilter if MediaPipe init fails.
	 */
	enableVirtualBackground = async (bg: VirtualBg): Promise<void> => {
		if (this.mediapipeBackground?.isActive) {
			await this.mediapipeBackground.setBackground(bg);
			return;
		}

		const deviceId: string | undefined = store.getState().VideoReducer?.selectedVideo?.deviceId;
		if (!this.publisher || !deviceId) return;

		try {
			this.mediapipeBackground = new MediaPipeBackground();

			const connector: OT.MediaProcessorConnector = {
				setTrack: async (rawTrack: MediaStreamTrack) => {
					if (!this.mediapipeBackground) {
						this.log('warn', 'MediaPipeBackground: connector.setTrack called but pipeline is null, passing through raw track', { function: 'enableVirtualBackground.setTrack' });
						return rawTrack;
					}
					try {
						const rawStream = new MediaStream([rawTrack]);
						const canvasStream = await this.mediapipeBackground.init(rawStream, bg);
						return canvasStream.getVideoTracks()[0];
					} catch (e) {
						this.log('error', `MediaPipeBackground: connector.setTrack failed, passing through raw track: ${e}`, { function: 'enableVirtualBackground.setTrack', error: e });
						this.mediapipeBackground?.destroy();
						this.mediapipeBackground = null;
						return rawTrack;
					}
				},
				destroy: async () => {
					try {
						this.mediapipeBackground?.destroy();
					} catch (e) {
						this.log('error', `MediaPipeBackground: connector.destroy failed: ${e}`, { function: 'enableVirtualBackground.destroy', error: e });
					}
					this.mediapipeBackground = null;
				},
			};

			await this.publisher.setVideoMediaProcessorConnector(connector);
			this.mediapipeViaConnector = true;
		} catch (e) {
			this.log(
			'error',
			`MediaPipeBackground: enableVirtualBackground failed, falling back to OT filter: ${e}`,
			{ function: 'enableVirtualBackground', error: e },
		);
			this.mediapipeBackground?.destroy();
			this.mediapipeBackground = null;
			this.mediapipeViaConnector = false;
			const filter = getCurrentVirtualBG();
			if (filter) this.publisher.applyVideoFilter(filter);
		}
	};

	/**
	 * Remove the virtual background and return the publisher to the raw camera.
	 *
	 * • Connector path: pass null to setVideoMediaProcessorConnector — OT
	 *   re-acquires the camera device and calls connector.destroy() to tear down
	 *   the MediaPipe pipeline.
	 * • Init path (publisher was created with the custom track): can't swap back
	 *   via connector, so put the pipeline into passthrough mode instead.
	 * • No active pipeline: clear any OT native videoFilter.
	 */
	disableVirtualBackground = async (): Promise<void> => {
		if (!this.mediapipeBackground?.isActive) {
			this.publisher?.clearVideoFilter();
			return;
		}

		// Keep the pipeline alive in passthrough mode rather than tearing it
		// down. Clearing the connector via setVideoMediaProcessorConnector(null)
		// makes OT re-acquire its internally tracked camera, which is stale if
		// the user switched cameras while VBG was active. Passthrough is cheap
		// (single WebGL blit, no segmentation) and the pipeline is properly
		// cleaned up on session destroy or next republish.
		this.mediapipeBackground.clearBackground();
	};

	stopPublishing = (streamId?: string) => {
		const publisherToStop = this.myPublishers.find(
			(p) => streamId && p.stream?.streamId === streamId,
		) as Publisher ?? this.publisher;
		if (publisherToStop && !publisherToStop?.isLoading()) {
			this.session.unpublish(publisherToStop);
			// unpublish removes the stream from the session but leaves the publisher's
			// RTCPeerConnection open — and once unpublished its `.stream` goes null, so the
			// restartPublisher teardown loop (which filters on `!!stream`) can never reclaim it.
			// The peer connections then accumulate across revoke/re-grant republishes until a
			// new publish can't complete ICE (OT_TIMEOUT / ICEWorkflow). Fully destroy a retired
			// publisher and drop it from the list. The active publisher is left intact to avoid a
			// dangling this.publisher reference. (BDEV-4780)
			if (publisherToStop !== this.publisher) {
				publisherToStop.destroy();
				this.myPublishers = this.myPublishers.filter((p) => p !== publisherToStop);
			}
		}
	};

	getImage = () => this.publisher?.getImgData();

	changeResolution = (height: number, width: number, framerate: number): void => {
		// When using the MediaPipe canvas pipeline, OT's videoSource is a canvas
		// track whose dimensions are fixed by the canvas element. applyConstraints
		// has no effect on canvas tracks, so skip it.
		if (this.mediapipeBackground?.isActive) return;

		const [lastWidth, lastHeight] = this.lastRes;

		// only update resolution when it's a change, otherwise ignore
		if ((lastWidth !== width || lastHeight !== height) || this.lastFrameRate !== framerate) {
			// set video track resolution by applying constraints to the video track if it exists
			if (this.publisher?._.webRtcStream()) {
				this.videoMediaStream?.getVideoTracks()[0]?.applyConstraints(
					{
						width: { ideal: 1920 },
						height: { ideal: 1080 },
						frameRate: { ideal: framerate, max: framerate },
					},
				);
			}
			this.lastRes = [1920, 1080];
			this.lastFrameRate = framerate;
			debugLog('changing resolution', this.lastRes);
		}
	};

	changeVideoStream = async (selectedVideo: string) => {
		if (!this.publisher) {
			throw new Error('publisher is not set');
		}

		// When the MediaPipe pipeline is active, OT's videoSource is a canvas
		// track that doesn't change between cameras. Instead, swap the raw
		// camera stream feeding the pipeline — the canvas (and OT feed) updates
		// automatically with no publisher restart required.
		if (this.mediapipeBackground?.isActive) {
			try {
				const newRawStream = await getCameraStreamNoAudio(selectedVideo);
				await this.mediapipeBackground.updateRawStream(newRawStream);
				return true;
			} catch (e) {
				this.log(
				'error',
				`MediaPipeBackground: could not switch camera source: ${e}`,
				{ function: 'changeVideoStream', error: e },
			);
				return false;
			}
		}

		let prevVideo: any;

		try {
			prevVideo = await this.publisher?.getVideoSource();
		} catch (e) {
			console.error('could not get previous video', e);
		}

		// can't set video source on publisher that isn't currently using one
		if (!prevVideo.deviceId) {
			this.stopPublishing();
			return true;
		}

		try {
			await this.publisher!.setVideoSource(selectedVideo);
			this.videoMediaStream = this.publisher?._.webRtcStream();
			return true;
		} catch (e) {
			if (prevVideo?.deviceId) {
				await this.changeVideoStream(prevVideo.deviceId);
			}
			console.error('could not change video', e);
			return false;
		}
	};

	changeAudioStream = async (selectedAudio: string) => {
		if (!this.publisher) {
			throw new Error('publisher is not set');
		}

		let prevAudio: any;

		try {
			prevAudio = await this.publisher?.getAudioSource();
		} catch (e) {
			console.error('could not get previous audio', e);
		}

		// can't set audio source on publisher that isn't currently using one
		if (!prevAudio) {
			this.stopPublishing();
			return true;
		}

		try {
			await this.publisher?.setAudioSource(selectedAudio);
			console.log('setSelectedAudio', selectedAudio);
			return true;
		} catch (e) {
			console.error('could not change audio', e);
		}

		return false;
	};

	// Derive the restricted-user state from which inputs are currently suspended
	// (track ended by a browser permission revoke while the publisher is kept alive).
	computeRestrictedState = (): number => {
		if (this.videoSuspended && this.audioSuspended) {
			return RESTRICTED_USER_STATES.CAMERA_AND_MICROPHONE;
		}
		if (this.videoSuspended) return RESTRICTED_USER_STATES.CAMERA;
		if (this.audioSuspended) return RESTRICTED_USER_STATES.MICROPHONE;
		return RESTRICTED_USER_STATES.NONE;
	};

	// Mark an input suspended and mute that side on the live publisher. Driven by the
	// Permissions API onchange — reliable every cycle, idempotent with the track listener.
	suspendInput = (kind: 'video' | 'audio') => {
		if (!this.publisher) return;
		if (kind === 'video') {
			this.videoSuspended = true;
			this.publisher.publishVideo(false);
		} else {
			this.audioSuspended = true;
			this.publisher.publishAudio(false);
		}
		store.dispatch(setRestrictedUserState(this.computeRestrictedState()));
	};

	// Re-attach an audio source to the live publisher after the mic permission is re-granted.
	// Camera recovery republishes instead (see setLocalVideo) — in-place restore renders black.
	restoreAudioSource = async (deviceId?: string): Promise<boolean> => {
		if (!this.publisher || !deviceId) return false;
		try {
			await this.publisher.setAudioSource(deviceId);
			this.publisher.publishAudio(true);
			// setAudioSource swaps in a NEW track; re-attach the { once: true } 'ended'
			// listener or a second mic revoke goes undetected.
			const [newAudioTrack] = this.publisher?._.webRtcStream()?.getAudioTracks() ?? [];
			this.attachAudioEndedListener(this.publisher, newAudioTrack);
			this.audioSuspended = false;
			store.dispatch(setRestrictedUserState(this.computeRestrictedState()));
			return true;
		} catch (e) {
			this.log('error', `restoreAudioSource failed: ${e}`, { function: 'restoreAudioSource', error: e });
			return false;
		}
	};

	// Re-attach the camera to the live publisher after the camera permission is re-granted,
	// WITHOUT a republish. A fresh publisher fails on re-grant: it calls getUserMedia for the
	// mic while the kept-alive publisher still holds the device (OT_CHROME_MICROPHONE_ACQUISITION_
	// ERROR / crbug 4799), and its new PeerConnection can't complete ICE alongside the still-
	// published one (OT_TIMEOUT). In-place restore reuses the existing PeerConnection, mirroring
	// restoreAudioSource. (BDEV-4780)
	restoreVideoSource = async (deviceId?: string): Promise<boolean> => {
		if (!this.publisher || !deviceId) return false;
		try {
			if (this.mediapipeBackground?.isActive) {
				// Virtual background active: hot-swap the raw camera into the running pipeline.
				// The canvas (generator) track OT publishes never changes, so there is no
				// renegotiation and the local self-feed <video> stays bound to a live track —
				// no rebind needed. Same primitive changeVideoStream uses for camera switches.
				const rawStream = await getCameraStreamNoAudio(deviceId);
				await this.mediapipeBackground.updateRawStream(rawStream);
			} else {
				// No virtual background: swap the camera track in place on the publisher.
				await this.publisher.setVideoSource(deviceId);
				this.videoMediaStream = this.publisher?._.webRtcStream();
				// setVideoSource swaps OT's internal track but does NOT change the srcObject
				// reference the self-feed <video> is bound to, so it keeps showing the ended
				// (black) track. Re-fire mainVideoStream with a fresh MediaStream built from the
				// publisher's current source tracks so the <video> re-attaches. stopOldTracks:false
				// because the audio track is shared with the outgoing stream and still live.
				const videoSource = await this.publisher.getVideoSource();
				const audioTrack = await this.publisher.getAudioSource?.();
				const liveTracks = [videoSource?.track, audioTrack].filter(
					(t): t is MediaStreamTrack => !!t,
				);
				store.dispatch(setMainVideoStream(new MediaStream(liveTracks), false));
			}
			this.publisher.publishVideo(true);
			this.videoSuspended = false;
			store.dispatch(setRestrictedUserState(this.computeRestrictedState()));
			// eslint-disable-next-line no-console
			console.log('[BDEV TEMP] restoreVideoSource OK, hasLiveVideo=' + this.hasLiveVideoTrack());
			return true;
		} catch (e) {
			// eslint-disable-next-line no-console
			console.log('[BDEV TEMP] restoreVideoSource THREW: ' + e);
			this.log('error', `restoreVideoSource failed: ${e}`, { function: 'restoreVideoSource', error: e });
			return false;
		}
	};

	stop = () => {
		this.publisher?.destroy();
		// Remove the mediaStopped/streamDestroyed listener before destroying so the
		// onDestroyed callback (setSharingScreen(false)) doesn't fire during a reconnect,
		// which would clear the sharingScreen flag before the new session can restart it.
		if (this.screensharePublisher) {
			this.screensharePublisher.off('mediaStopped streamDestroyed');
			this.screensharePublisher.destroy();
			this.screensharePublisher = null;
		}
		this.screenshareSourceTrack = null;
		this.screenshareSourceAudioTrack = null;
		this.destroy();
	};

	startSession() {
		return new Promise((resolve, reject) => {
			this.session.connect(this.token, (err) => {
				if (err) reject(err);
				else resolve(true);
			});
		});
	}

	startScreensharing = (
		onDestroyed: () => void,
		videoSource?: string | MediaStreamTrack,
		name?: string,
		audioSource?: MediaStreamTrack,
	) => new Promise((resolve, reject) => {
		this.log(
			'info',
			'publish screensharing',
			{
				function: 'startScreensharing',
			},
		);

		// Ensure only one screenshare publisher exists at a time.
		if (this.screensharePublisher) {
			this.screensharePublisher.off('mediaStopped streamDestroyed');
			this.screensharePublisher.destroy();
			this.screensharePublisher = null;
		}

		// When a custom track is provided, store it so getScreenshareTrack() can clone it
		// directly on future reconnects instead of using the internal webRtcStream() API,
		// which is unreliable for publishers created with a custom videoSource.
		this.screenshareSourceTrack = videoSource instanceof MediaStreamTrack ? videoSource : null;
		this.screenshareSourceAudioTrack = audioSource ?? null;

		let interval: NodeJS.Timeout | undefined;

		this.screensharePublisher = OT.initPublisher(undefined, {
			insertDefaultUI: false,
			audioFallback: {publisher: false, subscriber: false},
			// enableDtx is false due to this ticket: https://brandlive.atlassian.net/browse/GREEN-5042
			enableDtx: false,
			// When reusing an existing MediaStreamTrack (reconnect path), OpenTok skips
			// getDisplayMedia and calls getUserMedia for audio instead. Only enable audio if
			// a specific audio source track was provided (e.g. tab audio); otherwise disable
			// it to prevent the publisher from capturing the mic as a fallback.
			publishAudio: videoSource instanceof MediaStreamTrack ? !!audioSource : true,
			...(videoSource instanceof MediaStreamTrack && { audioSource: audioSource ?? false }),
			showControls: false,
			frameRate: 30,
			videoSource: videoSource || 'screen',
			name: name || String(this.user?.gr_user),
			audioBitrate: 64000,
			disableAudioProcessing: true,
			enableStereo: true,
		}) as Publisher;

		if (this.screensharePublisher) {
			this.myPublishers.push(this.screensharePublisher);
			this.publisherListeners(this.screensharePublisher, true);
		}

		this.screensharePublisher.on('mediaStopped streamDestroyed', onDestroyed);
		this.session.publish(this.screensharePublisher, (err?: OT.OTError) => {
			if (err) {
				this.log(
					'error',
					`publish screensharing error ${err?.name} ${err?.message}`,
					{
						function: 'startScreensharing',
					},
				);
				reject(err);
			} else {
				interval = setInterval(() => {
					if (this.screensharePublisher!.streamId) {
						clearInterval(interval as NodeJS.Timeout);
						resolve(this.screensharePublisher);
					}
				}, 150);
			}
		});
	});

	stopScreensharing = () => {
		this.log(
			'info',
			'stopping screensharing',
			{
				function: 'stopScreensharing',
			},
		);
		// prop exists on screensharePublisher but the type doesn't include it.
		const id = this.screensharePublisher?.streamId;
		this.screensharePublisher?.destroy();
		return id;
	};

	// Returns a cloned screen capture track; clone prevents publisher.destroy() from stopping
	// the track before the next publisher can use it.
	getScreenshareTrack = (): MediaStreamTrack | null => {
		// Use stored source track when available, fall back to webRtcStream() for getDisplayMedia-acquired tracks.
		const track: MediaStreamTrack | null | undefined = this.screenshareSourceTrack
			?? (this.screensharePublisher as any)?._.webRtcStream()?.getVideoTracks()[0];
		if (!track) return null;
		const clone = track.clone();
		clone.contentHint = 'detail';
		return clone;
	};

	// Returns a clone of the live audio track from the current screenshare publisher, or null
	// if the screenshare has no audio (screen-only or no tab audio was shared).
	getScreenshareAudioTrack = (): MediaStreamTrack | null => {
		const track: MediaStreamTrack | null | undefined = this.screenshareSourceAudioTrack
			?? (this.screensharePublisher as any)?._.webRtcStream()?.getAudioTracks()[0];
		if (!track) return null;
		return track.clone();
	};

	getRTCFrames = async (
		user: Subscriber | Publisher,
	): Promise<number> => {
		try {
			const getFrames = async () => {
				const userRTCStats = await user.getRtcStatsReport();
				let returnedFrames = 0;
				if (userRTCStats instanceof Map || userRTCStats instanceof RTCStatsReport) {
					userRTCStats.forEach((rep: any) => {
						if ((rep.type === 'track' || rep.type === 'inbound-rtp') && rep.kind === 'video') {
							returnedFrames = rep.framesReceived;
						}
					});
				} else {
					if(Array.isArray(userRTCStats)) {
						userRTCStats[0].rtcStatsReport.forEach((rep: any) => {
							if (rep.type === 'media-source' && rep.kind === 'video') {
								returnedFrames = rep.frames;
							}
						});
					}
				}
				return returnedFrames;
			};
			const frames = await getFrames().catch(() => 0);
			return frames ?? 0;
		} catch (e) {
			this.log(
				'error',
				`getRTCFrames error ${e}`,
				{
					function: 'getRTCFrames',
				},
			);
			console.error(e);
			return 0;
		}
	};

	// get stats for all publishers and subscribers and send them as logs to Datadog
	logOpentokStats = async (): Promise<void> => {
		try {
			if (!this.session.isConnected()) return;
			const opentokStats: { [key: string]: OT.SubscriberStats | OT.PublisherStatsArr } = { };
			const allPubsAndSubs = [...this.myPublishers, ...Object.values(this.subscribers)];

			const promises: Promise<void>[] = [];
			allPubsAndSubs.forEach((pubOrSub) => {
				promises.push(getPubOrSubStats(pubOrSub).then((stats) => {
					if (!stats || !pubOrSub.stream) return;
					opentokStats[pubOrSub.stream.streamId] = stats;
				}));
			});

			await Promise.all(promises);
			console.log('logOpentokStats', { opentokStats });
		} catch (e) {
			console.error(`logOpentokStats error: ${e}`);
		}
	};

	/**
	 * Snapshot of this client's own OpenTok quality for the health dashboard.
	 *
	 * Everything here is browser-local: the Vonage server APIs do not expose
	 * per-connection quality, so a dashboard in another tab has to ask each client.
	 * Greenroom runs routed sessions, so publisher.getStats() returns a single
	 * element and its connectionId/subscriberId are not populated - per-subscriber
	 * detail can only come from each subscriber's own getStats().
	 */
	collectHealthStats = async (): Promise<{
		publishers: PublisherHealth[];
		subscribers: SubscriberHealth[];
		cpuPerformanceState?: CpuPerformanceState;
		sessionId?: string;
	}> => {
		const publishers: PublisherHealth[] = [];
		const subscribers: SubscriberHealth[] = [];

		if (!this.session?.isConnected()) {
			return { publishers, subscribers, cpuPerformanceState: this.cpuPerformanceState };
		}

		await Promise.all([
			...this.myPublishers.map(async (publisher) => {
				const stats = await getPubOrSubStats(publisher) as OT.PublisherStatsArr | undefined;
				// Routed session: a single container, so index 0 is the whole picture.
				const container = Array.isArray(stats) ? stats[0] : undefined;
				if (!container?.stats) return;

				const { video, mediaLink } = container.stats as any;
				publishers.push({
					streamId: publisher.stream?.streamId,
					transport: toTransportHealth(mediaLink?.transport),
					videoBitrate: video?.bytesSent ?? 0,
					frameRate: video?.frameRate ?? 0,
					packetsLost: video?.packetsLost ?? 0,
					packetsSent: video?.packetsSent ?? 0,
					layers: video?.layers?.map((layer: any) => ({
						width: layer.width,
						height: layer.height,
						encodedFrameRate: layer.encodedFrameRate,
						qualityLimitationReason: layer.qualityLimitationReason,
					})),
				});
			}),
			...Object.values(this.subscribers).map(async (subscriber) => {
				const streamId = subscriber.stream?.streamId;
				if (!streamId) return;

				const stats = await getPubOrSubStats(subscriber) as OT.SubscriberStats | undefined;
				if (!stats) return;

				const { video, mediaLink } = stats as any;
				const score = this.subscriberQualityScores[streamId];
				subscribers.push({
					streamId,
					streamName: subscriber.stream?.name,
					transport: toTransportHealth(mediaLink?.transport),
					remotePublisherTransport: toTransportHealth(mediaLink?.remotePublisherTransport),
					networkDegradationSource: mediaLink?.networkDegradationSource,
					width: video?.width ?? 0,
					height: video?.height ?? 0,
					frameRate: video?.frameRate ?? 0,
					bitrate: video?.bitrate ?? 0,
					packetsLost: video?.packetsLost ?? 0,
					freezeCount: video?.freezeCount,
					totalFreezesDuration: video?.totalFreezesDuration,
					audioQualityScore: score?.audioQualityScore,
					videoQualityScore: score?.videoQualityScore,
				});
			}),
		]);

		return {
			publishers,
			subscribers,
			cpuPerformanceState: this.cpuPerformanceState,
			sessionId: this.session?.sessionId,
		};
	};

	// for bitrate, we can only check the total number of downloaded bytes per subscriber and total
	// number of uploaded bytes for the publisher. so here we get what the current number of
	// downloaded/uploaded bytes is, subtract the last number of downloaded/uploaded bytes from that
	// then divide by the number of seconds since the last check (to get bits per second) and divide
	// by 1024 for kilobits per second.
	parseStats = (stats: RawStats, user: Subscriber | Publisher) => {
		const elapsedSeconds = (stats.lastTime - this.lastStats[stats.id].time) / 1000;

		// packet loss ratio is calculated as a percentage of the packets lost out of the expected
		// packets sent or received. we use the packets lost and sent/received in the current
		// interval (last - current) to provide a more useful metric than total packet loss.
		const periodVideoPacketLoss = stats.videoPacketsLost
			- this.lastStats[stats.id].videoPacketsLost;
		const periodVideoPackets = stats.videoPackets
			- this.lastStats[stats.id].videoPackets;
		const periodAudioPacketLoss = stats.audioPacketsLost
			- this.lastStats[stats.id].audioPacketsLost;
		const periodAudioPackets = stats.audioPackets - this.lastStats[stats.id].audioPackets;

		return {
			videoBitrate: Math.floor(
				(
					(stats.videoBits - this.lastStats[stats.id].videoBits)
					/ elapsedSeconds
				) / 1024,
			),
			videoPacketLossRatio: periodVideoPacketLoss > 0
				? periodVideoPacketLoss / periodVideoPackets
				: 0,
			audioBitrate: Math.floor(
				(
					(stats.audioBits - this.lastStats[stats.id].audioBits)
					/ elapsedSeconds
				) / 1024,
			),
			audioPacketLossRatio: periodAudioPacketLoss > 0
				? periodAudioPacketLoss / periodAudioPackets
				: 0,
			frameRate: ((stats.frames - this.lastStats[stats.id].frames) / elapsedSeconds),
			resolution: user.stream?.hasVideo
				? { width: user.videoWidth() ?? 0, height: user.videoHeight() ?? 0 }
				: { width: 0, height: 0 },
			guid: user.stream?.name?.includes(',') ? user.stream?.name.split(',')[1] : '0',
		};
	};

	printOTDebugData = () => {
		if (this.debugOpentokSubscriptions) {
			if (this.lastSubscriberState !== this.subscribers && window.debugOT) {
				// clean up the subscribes, unsubscribes, resubscribes
				// (as users change we don't want to run oom)
				window.debugOT.resubscribes = {};
				window.debugOT.subscribes = {};
				window.debugOT.unsubscribes = {};
			}

			// logic to allow us to debug each subscriber (updated list)
			Object.entries(this.subscribers).forEach(([, subscriber]) => {
				if (this.debugOpentokSubscriptions && this.subscribers !== this.lastSubscriberState) {
					if (subscriber?.stream) {
						const { stream } = subscriber;
						const { streamId } = subscriber.stream;
						printOTDebugLogForStreamId(
							streamId,
							{
								subscriber,
							},
							() => {
								console.log(`manually resubscribing to ${streamId}`);
								this.resubscribeToStream(subscriber);
							},
							() => {
								console.log(`manually unsubscribing from ${streamId}`);
								this.session.unsubscribe(subscriber);
							},
							() => {
								console.log(`manually subscribing to ${streamId}`);
								this.subscribeToStream(stream);
							},
						);
					}
				}
			});
			this.lastSubscriberState = this.subscribers;
		}
	};

	getOTStats = async (): Promise<TotalOTStats> => {
		const totalStats: TotalOTStats = {};
		try {
			if (this.debugOpentokSubscriptions && window.debugOT) {
				this.printOTDebugData();
				window.debugOT.subscribers = this.subscribers;
			}
			Object.entries(this.subscribers).forEach(async ([, subscriber]) => {
				// OpenTok's getStats method does not work with screensharing
				if (subscriber?.stream?.videoType !== 'screen') {
					const newStats = await this.getSubscriberStats(subscriber);

					newStats.frames = subscriber.stream ? await this.getRTCFrames(subscriber) : 0;

					if (this.lastStats[newStats.id] && newStats.id !== 'null') {
						totalStats[newStats.id] = this.parseStats(newStats, subscriber);
					}

					this.lastStats[newStats.id] = {
						videoBits: newStats.videoBits,
						videoPackets: newStats.videoPackets,
						videoPacketsLost: newStats.videoPacketsLost,
						audioBits: newStats.audioBits,
						audioPackets: newStats.audioPackets,
						audioPacketsLost: newStats.audioPacketsLost,
						frames: newStats.frames,
						time: newStats.lastTime,
					};
				}
			});

			if (this.publisher) {
				const newStat = await this.getPublisherStats(this.publisher);
				newStat.frames = this.publisher?.stream ? await this.getRTCFrames(this.publisher) : 0;

				if (this.lastStats[newStat.id] && newStat.id !== 'null') {
					totalStats[newStat.id] = this.parseStats(newStat, this.publisher);
				}

				this.lastStats[newStat.id] = {
					videoBits: newStat.videoBits,
					videoPackets: newStat.videoPackets,
					videoPacketsLost: newStat.videoPacketsLost,
					audioBits: newStat.audioBits,
					audioPackets: newStat.audioPackets,
					audioPacketsLost: newStat.audioPacketsLost,
					frames: newStat.frames,
					time: newStat.lastTime,
				};

				if (totalStats[this.publisher.streamId]) {
					setBandwidth(totalStats[this.publisher.streamId].videoBitrate);
				}
			}
		} catch (e) {
			this.log(
				'error',
				`getOTStats error ${e}`,
				{
					function: 'getOTStats',
				},
			);
			console.error(e);
		}
		return totalStats;
	};

	/**
	 * This was added to fix a bug where people with muted cam feed not being
	 * heard by people who were in the room before they joined.
	 */
	resubscribeToStreamIfVideoMuted = (streamId: string) => {
		const sub = this.subscribers[streamId];

		if (!sub || !sub?.stream) return false; // this isn't good

		const { stream } = sub;
		const {
			hasVideo,
			// @ts-ignore
			channel: channels,
		} = stream;

		const audioChannel = channels.find((c: { type: string }) => c.type === 'audio');

		if (hasVideo || !audioChannel) return false; // we will not need to resubscribe

		if (this.debugOpentokSubscriptions) {
			console.log('resubscribing to stream with webcam off ', streamId);
		}

		// unsubscribe to old stream
		this.session.unsubscribe(sub);
		if (!this.subscribers[streamId].stream) delete this.subscribers[streamId];

		if (stream) {
			// now subscribe again to them
			this.subscribeToStream(stream);
		}
		return false;
	};

	forceSetAudio = (streamId: string, value: boolean) => {
		// value in this context is true if unmuted, and false if muted (the prescence of audio)
		const stream = this.streams.get(streamId);

		if (stream) {
			this.session.signal({
				type: OPENTOK_CONSTANTS.SIGNALS.FORCE_SET_AUDIO,
				data: JSON.stringify({ streamId, value }),
				to: stream.connection,
			}, () => { });
		}
	};

	forceSetVideo = (streamId: string, value: boolean) => {
		// value in this context is true if unmuted, and false if muted (the prescence of video)
		const stream = this.streams.get(streamId);

		if (stream) {
			this.session.signal({
				type: OPENTOK_CONSTANTS.SIGNALS.FORCE_SET_VIDEO,
				data: JSON.stringify({ streamId, value }),
				to: stream.connection,
			}, () => {});
		}
	};

	setLocalAudio = (active: boolean) => {
		// active is true if audio is unmuted, false if muted
		if (this.debugOpentokSubscriptions) {
			console.log(`OTClass setting local audio to ${active} for publisher: ${this.publisher?.streamId}`);
		}

		// todo this is where we could show an error to the user if publisher is false
		// and not set the local av state - is this something we should do anyways?
		this.publisher?.publishAudio(active);
	};

	// True when the publisher currently has a live outgoing video track. A publisher can be
	// audio-only (published while the camera was denied/prompt, or a revoke ended the track) —
	// in that case there is no video track to restore in place, so recovery must republish.
	hasLiveVideoTrack = (): boolean => !!this.publisher?._.webRtcStream()
		?.getVideoTracks().some((t: MediaStreamTrack) => t.readyState === 'live');

	setLocalVideo = (active: boolean) => {
		// active is true if video is enabled, false if disabled
		if (this.debugOpentokSubscriptions) {
			console.log(`OTClass setting local video to ${active} for publisher: ${this.publisher?.streamId}`);
		}
		// No live camera track (revoked or never acquired) -> republish; an in-place re-acquire never
		// rebinds the local self-feed's <video> (renders black). MediaPipe re-acquires below instead.
		if (active && !this.mediapipeBackground?.isActive) {
			if (this.videoSuspended || !this.hasLiveVideoTrack()) {
				store.dispatch(setShouldRepublishVideo(true));
				return;
			}
		}

		this.publisher?.publishVideo(active);

		if (this.mediapipeBackground?.isActive) {
			if (!active) {
				// Stop raw camera tracks so the OS releases the camera hardware
				// (e.g. macOS camera indicator light turns off).
				this.mediapipeBackground.stopRawStream();
			} else {
				// Re-acquire the camera and feed it back into the pipeline.
				const deviceId = store.getState().VideoReducer?.selectedVideo?.deviceId;
				if (deviceId) {
					getCameraStreamNoAudio(deviceId)
						.then((stream) => this.mediapipeBackground?.updateRawStream(stream))
						.catch((e) => this.log('error', `MediaPipeBackground: failed to re-acquire camera on video enable: ${e}`, { function: 'setLocalVideo', error: e }));
				}
			}
		}
	};
}
